export const SCREENSHOT_GALLERY_AUTH_SCENARIO_STORAGE_KEY =
  'wavvest:screenshot-gallery:auth-scenario';
export const SCREENSHOT_GALLERY_RETRY_DELAY_STORAGE_KEY =
  'wavvest:screenshot-gallery:signup-retry-delay-ms';

export type ScreenshotGalleryAuthScenario =
  | 'signup-email-field-failure'
  | 'signup-invalid-input'
  | 'signup-rate-limited'
  | 'signup-transient-retry'
  | 'signup-unexpected-next-step'
  | 'confirmation-code-mismatch'
  | 'confirmation-expired-code'
  | 'confirmation-rate-limited'
  | 'confirmation-completion-failure'
  | 'confirmation-unexpected-next-step'
  | 'resend-rate-limited'
  | 'resend-invalid-parameter'
  | 'resend-user-not-found'
  | 'login-wrong-credentials'
  | 'login-user-not-found'
  | 'login-unconfirmed-account'
  | 'login-legacy-password-reset'
  | 'login-account-setup-incomplete'
  | 'login-profile-support-required'
  | 'login-profile-unavailable'
  | 'login-auth-required'
  | 'forgot-password-user-not-found'
  | 'forgot-password-rate-limited'
  | 'forgot-password-invalid-email'
  | 'forgot-password-pending-verification'
  | 'forgot-password-verification-code-mismatch'
  | 'forgot-password-verification-expired-code'
  | 'forgot-password-reset-code-mismatch'
  | 'forgot-password-reset-expired-code'
  | 'forgot-password-reset-invalid-password'
  | 'forgot-password-reset-rate-limited';

type CognitoScenarioError = {
  code?: string;
  message: string;
  name?: string;
  response?: {
    status?: number;
  };
  status?: number;
};

const scenarioAliases: Record<string, ScreenshotGalleryAuthScenario> = {
  signup_submit_email: 'signup-email-field-failure',
  signup_submit_email_field: 'signup-email-field-failure',
  signup_submit_email_field_failure: 'signup-email-field-failure',
  signup_email_exists: 'signup-email-field-failure',
  signup_invalid_input: 'signup-invalid-input',
  signup_submit_invalid_input: 'signup-invalid-input',
  signup_rate_limited: 'signup-rate-limited',
  signup_submit_rate_limited: 'signup-rate-limited',
  signup_transient: 'signup-transient-retry',
  signup_submit_transient: 'signup-transient-retry',
  signup_submit_transient_retry: 'signup-transient-retry',
  signup_unexpected_next_step: 'signup-unexpected-next-step',
  signup_submit_unexpected_next_step: 'signup-unexpected-next-step',
  confirmation_invalid_code: 'confirmation-code-mismatch',
  confirmation_code_mismatch: 'confirmation-code-mismatch',
  confirmation_expired_code: 'confirmation-expired-code',
  confirmation_rate_limited: 'confirmation-rate-limited',
  confirmation_completion_failure: 'confirmation-completion-failure',
  confirmation_signup_completion_failure: 'confirmation-completion-failure',
  confirmation_unexpected_next_step: 'confirmation-unexpected-next-step',
  resend_rate_limited: 'resend-rate-limited',
  resend_invalid_parameter: 'resend-invalid-parameter',
  resend_user_not_found: 'resend-user-not-found',
  login_wrong_credentials: 'login-wrong-credentials',
  login_user_not_found: 'login-user-not-found',
  login_unconfirmed: 'login-unconfirmed-account',
  login_unconfirmed_account: 'login-unconfirmed-account',
  login_legacy_password_reset: 'login-legacy-password-reset',
  login_account_setup_incomplete: 'login-account-setup-incomplete',
  login_profile_support: 'login-profile-support-required',
  login_profile_support_required: 'login-profile-support-required',
  login_profile_unavailable: 'login-profile-unavailable',
  login_auth_required: 'login-auth-required',
  forgot_password_user_not_found: 'forgot-password-user-not-found',
  forgot_password_rate_limited: 'forgot-password-rate-limited',
  forgot_password_invalid_email: 'forgot-password-invalid-email',
  forgot_password_pending_verification: 'forgot-password-pending-verification',
  forgot_password_verification_invalid_code: 'forgot-password-verification-code-mismatch',
  forgot_password_verification_code_mismatch: 'forgot-password-verification-code-mismatch',
  forgot_password_verification_expired_code: 'forgot-password-verification-expired-code',
  forgot_password_reset_invalid_code: 'forgot-password-reset-code-mismatch',
  forgot_password_reset_code_mismatch: 'forgot-password-reset-code-mismatch',
  forgot_password_reset_expired_code: 'forgot-password-reset-expired-code',
  forgot_password_reset_invalid_password: 'forgot-password-reset-invalid-password',
  forgot_password_reset_rate_limited: 'forgot-password-reset-rate-limited',
};

const canonicalScenarios = new Set<ScreenshotGalleryAuthScenario>([
  'signup-email-field-failure',
  'signup-invalid-input',
  'signup-rate-limited',
  'signup-transient-retry',
  'signup-unexpected-next-step',
  'confirmation-code-mismatch',
  'confirmation-expired-code',
  'confirmation-rate-limited',
  'confirmation-completion-failure',
  'confirmation-unexpected-next-step',
  'resend-rate-limited',
  'resend-invalid-parameter',
  'resend-user-not-found',
  'login-wrong-credentials',
  'login-user-not-found',
  'login-unconfirmed-account',
  'login-legacy-password-reset',
  'login-account-setup-incomplete',
  'login-profile-support-required',
  'login-profile-unavailable',
  'login-auth-required',
  'forgot-password-user-not-found',
  'forgot-password-rate-limited',
  'forgot-password-invalid-email',
  'forgot-password-pending-verification',
  'forgot-password-verification-code-mismatch',
  'forgot-password-verification-expired-code',
  'forgot-password-reset-code-mismatch',
  'forgot-password-reset-expired-code',
  'forgot-password-reset-invalid-password',
  'forgot-password-reset-rate-limited',
]);

const getProcessEnvValue = (key: string): string | undefined => {
  if (typeof process === 'undefined') {
    return undefined;
  }

  return process.env?.[key];
};

const getScreenshotGalleryEnvValue = (): string | undefined =>
  import.meta.env?.VITE_SCREENSHOT_GALLERY || getProcessEnvValue('VITE_SCREENSHOT_GALLERY');

const normalizeScenarioValue = (value: string | null | undefined) =>
  value?.trim().toLowerCase().replace(/-/g, '_') || '';

const readSearchParam = (names: string[]): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const searchParams = new URLSearchParams(window.location.search);
  for (const name of names) {
    const value = searchParams.get(name);
    if (value) {
      return value;
    }
  }

  return null;
};

const writeLocalStorageValue = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Gallery seams are best-effort and must never affect production flows.
  }
};

const readLocalStorageValue = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

export const isScreenshotGalleryEnabled = (): boolean => {
  const value = getScreenshotGalleryEnvValue();
  return value === '1' || value === 'true';
};

export const getScreenshotGalleryAuthScenario = (): ScreenshotGalleryAuthScenario | null => {
  if (!isScreenshotGalleryEnabled() || typeof window === 'undefined') {
    return null;
  }

  const queryValue = readSearchParam(['authScenario', 'screenshotAuthScenario', 'sgAuthScenario']);
  if (queryValue) {
    writeLocalStorageValue(SCREENSHOT_GALLERY_AUTH_SCENARIO_STORAGE_KEY, queryValue);
  }

  const rawValue =
    queryValue || readLocalStorageValue(SCREENSHOT_GALLERY_AUTH_SCENARIO_STORAGE_KEY);
  const normalizedValue = normalizeScenarioValue(rawValue);
  const aliasedScenario = scenarioAliases[normalizedValue];
  if (aliasedScenario) {
    return aliasedScenario;
  }

  const canonicalValue = normalizedValue.replace(/_/g, '-') as ScreenshotGalleryAuthScenario;
  return canonicalScenarios.has(canonicalValue) ? canonicalValue : null;
};

export const getScreenshotGalleryRetryDelayMs = (defaultDelayMs: number): number => {
  if (!isScreenshotGalleryEnabled() || typeof window === 'undefined') {
    return defaultDelayMs;
  }

  const queryValue = readSearchParam([
    'signupRetryDelayMs',
    'screenshotRetryDelayMs',
    'sgRetryDelayMs',
  ]);
  if (queryValue) {
    writeLocalStorageValue(SCREENSHOT_GALLERY_RETRY_DELAY_STORAGE_KEY, queryValue);
  }

  const rawValue = queryValue || readLocalStorageValue(SCREENSHOT_GALLERY_RETRY_DELAY_STORAGE_KEY);
  const delayMs = Number(rawValue);
  if (!Number.isFinite(delayMs) || delayMs < 0) {
    return defaultDelayMs;
  }

  return delayMs;
};

export const isConfirmationScenario = (scenario: ScreenshotGalleryAuthScenario | null): boolean =>
  scenario === 'confirmation-code-mismatch' ||
  scenario === 'confirmation-expired-code' ||
  scenario === 'confirmation-rate-limited' ||
  scenario === 'confirmation-unexpected-next-step' ||
  scenario === 'resend-rate-limited' ||
  scenario === 'resend-invalid-parameter' ||
  scenario === 'resend-user-not-found';

export const isForgotPasswordVerificationScenario = (
  scenario: ScreenshotGalleryAuthScenario | null,
): boolean =>
  scenario === 'forgot-password-verification-code-mismatch' ||
  scenario === 'forgot-password-verification-expired-code';

export const buildScreenshotGallerySignUpError = (
  scenario: ScreenshotGalleryAuthScenario,
): CognitoScenarioError | null => {
  switch (scenario) {
    case 'signup-invalid-input':
      return {
        name: 'InvalidParameterException',
        message: 'Invalid signup input for screenshot gallery',
      };
    case 'signup-rate-limited':
      return {
        name: 'LimitExceededException',
        message: 'Too many signup attempts for screenshot gallery',
      };
    case 'signup-transient-retry':
      return {
        name: 'NetworkError',
        message: 'Network request failed during screenshot gallery signup',
      };
    default:
      return null;
  }
};

export const buildScreenshotGalleryConfirmationError = (
  scenario: ScreenshotGalleryAuthScenario,
): CognitoScenarioError | null => {
  switch (scenario) {
    case 'confirmation-code-mismatch':
    case 'forgot-password-verification-code-mismatch':
      return {
        name: 'CodeMismatchException',
        message: 'Invalid verification code for screenshot gallery',
      };
    case 'confirmation-expired-code':
    case 'forgot-password-verification-expired-code':
      return {
        name: 'ExpiredCodeException',
        message: 'Expired verification code for screenshot gallery',
      };
    case 'confirmation-rate-limited':
      return {
        name: 'LimitExceededException',
        message: 'Too many verification attempts for screenshot gallery',
      };
    default:
      return null;
  }
};

export const buildScreenshotGalleryForgotPasswordError = (
  scenario: ScreenshotGalleryAuthScenario,
): CognitoScenarioError | null => {
  switch (scenario) {
    case 'forgot-password-user-not-found':
      return {
        name: 'UserNotFoundException',
        message: 'User not found for screenshot gallery',
      };
    case 'forgot-password-rate-limited':
      return {
        name: 'LimitExceededException',
        message: 'Too many password reset attempts for screenshot gallery',
      };
    case 'forgot-password-invalid-email':
      return {
        name: 'InvalidParameterException',
        message: 'Invalid password reset input for screenshot gallery',
      };
    default:
      return null;
  }
};

export const buildScreenshotGalleryConfirmForgotPasswordError = (
  scenario: ScreenshotGalleryAuthScenario,
): CognitoScenarioError | null => {
  switch (scenario) {
    case 'forgot-password-reset-code-mismatch':
      return {
        name: 'CodeMismatchException',
        message: 'Invalid password reset code for screenshot gallery',
      };
    case 'forgot-password-reset-expired-code':
      return {
        name: 'ExpiredCodeException',
        message: 'Expired password reset code for screenshot gallery',
      };
    case 'forgot-password-reset-invalid-password':
      return {
        name: 'InvalidPasswordException',
        message: 'Invalid password for screenshot gallery',
      };
    case 'forgot-password-reset-rate-limited':
      return {
        name: 'LimitExceededException',
        message: 'Too many password reset confirmation attempts for screenshot gallery',
      };
    default:
      return null;
  }
};
